import React, { useState, useEffect } from 'react';
import {
  Card, Table, Badge, Button, Form, InputGroup,
  Dropdown, ButtonGroup, Spinner, Alert, Row, Col,
  ProgressBar, OverlayTrigger, Tooltip
} from 'react-bootstrap';
import {
  FaSearch, FaFilter, FaFileContract, FaEye,
  FaExclamationTriangle, FaCheckCircle, FaClock,
  FaHome, FaHandshake, FaKey, FaCheck,
  FaEnvelope, FaDownload, FaSync, FaFileAlt,
  FaSort, FaSortUp, FaSortDown, FaUser, FaMapMarkerAlt
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import BrokerApiService from '../../services/brokerApiService';

interface Transaction {
  id: string;
  agent_id: string;
  agent_name: string;
  agent_email: string;
  agent_photo?: string;
  property_address: string;
  property_city: string;
  property_state: string;
  property_price?: number;
  transaction_type: 'listing' | 'buying' | 'purchase';
  type?: 'listing' | 'buying' | 'purchase' | 'rental' | 'referral';
  stage: 'listing' | 'under_contract' | 'closing' | 'closed' | 'pre_contract' | 'showing' | 'completed';
  compliance_status: 'compliant' | 'warning' | 'critical' | 'pending';
  compliance_score: number;
  document_count: number;
  total_documents: number;
  days_in_stage: number;
  total_days: number;
  last_activity: string;
  last_activity_date: Date;
  client_name: string;
  client_email: string;
  estimated_closing_date?: Date;
  created_at: Date;
  updated_at?: Date;
  missing_documents: string[];
  pending_signatures: number;
  documents_count?: number;
}

interface TransactionStats {
  total_active: number;
  listing_count: number;
  under_contract_count: number;
  closing_count: number;
  closed_today: number;
  compliance_alerts: number;
  average_compliance_score: number;
  documents_needing_review: number;
}

const TransactionManagement: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [stats, setStats] = useState<TransactionStats | null>(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [complianceFilter, setComplianceFilter] = useState<string>('all');
  const [agentFilter, setAgentFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<string>('last_activity_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Removed modal states - now using full page routing

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [transactions, searchQuery, stageFilter, complianceFilter, agentFilter, sortField, sortDirection]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      
      // Get brokerage ID from current user
      const userBrokerage = await BrokerApiService.getCurrentUserBrokerage();
      if (!userBrokerage) {
        setError('No brokerage found for current user');
        setLoading(false);
        return;
      }
      
      const brokerageId = userBrokerage.brokerageId;
      console.log('Fetching transactions for brokerage:', brokerageId);
      
      // Fetch real data from broker API
      const response = await BrokerApiService.getTransactions(brokerageId);
      
      if (response.success && response.transactions) {
        // Set transaction data
        const transactionsData = response.transactions.map((t: any) => ({
          ...t,
          // Ensure dates are Date objects
          last_activity_date: new Date(t.last_activity_date),
          created_at: new Date(t.created_at),
          estimated_closing_date: t.estimated_closing_date ? new Date(t.estimated_closing_date) : undefined,
          // Default values for missing fields
          compliance_status: t.compliance_status || 'pending',
          compliance_score: t.compliance_score || 0,
          missing_documents: t.missing_documents || [],
          pending_signatures: t.pending_signatures || 0,
          agent_photo: t.agent_photo || undefined,
          last_activity: t.last_activity || 'Transaction created'
        }));
        
        setTransactions(transactionsData);
        
        // Set stats if provided
        if (response.stats) {
          setStats(response.stats);
        } else {
          // Calculate stats from transactions if not provided
          const activeTransactions = transactionsData.filter((t: Transaction) => t.stage !== 'closed');
          const stats: TransactionStats = {
            total_active: activeTransactions.length,
            listing_count: transactionsData.filter((t: Transaction) => t.stage === 'listing').length,
            under_contract_count: transactionsData.filter((t: Transaction) => t.stage === 'under_contract').length,
            closing_count: transactionsData.filter((t: Transaction) => t.stage === 'closing').length,
            closed_today: transactionsData.filter((t: Transaction) => 
              t.stage === 'closed' && 
              new Date(t.updated_at || t.created_at).toDateString() === new Date().toDateString()
            ).length,
            compliance_alerts: transactionsData.filter((t: Transaction) => 
              ['warning', 'critical'].includes(t.compliance_status)
            ).length,
            average_compliance_score: transactionsData.length > 0
              ? Math.round(transactionsData.reduce((sum: number, t: any) => sum + (t.compliance_score || 0), 0) / transactionsData.length)
              : 0,
            documents_needing_review: transactionsData.reduce((sum: number, t: any) => sum + (t.missing_documents?.length || 0), 0)
          };
          setStats(stats);
        }
        
        setError(null);
      } else {
        setError('Failed to load transactions');
      }
    } catch (err: any) {
      console.error('Error fetching transactions:', err);
      setError(err.message || 'Failed to load transactions');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.property_address.toLowerCase().includes(query) ||
        t.agent_name.toLowerCase().includes(query) ||
        t.client_name.toLowerCase().includes(query) ||
        t.property_city.toLowerCase().includes(query)
      );
    }

    // Stage filter
    if (stageFilter !== 'all') {
      filtered = filtered.filter(t => t.stage === stageFilter);
    }

    // Compliance filter
    if (complianceFilter !== 'all') {
      filtered = filtered.filter(t => t.compliance_status === complianceFilter);
    }

    // Agent filter
    if (agentFilter !== 'all') {
      filtered = filtered.filter(t => t.agent_id === agentFilter);
    }

    // Sorting
    filtered.sort((a, b) => {
      let aVal: any, bVal: any;

      switch (sortField) {
        case 'property_address':
          aVal = a.property_address;
          bVal = b.property_address;
          break;
        case 'stage':
          aVal = a.stage;
          bVal = b.stage;
          break;
        case 'compliance_score':
          aVal = a.compliance_score;
          bVal = b.compliance_score;
          break;
        case 'last_activity_date':
          aVal = a.last_activity_date;
          bVal = b.last_activity_date;
          break;
        default:
          aVal = a.last_activity_date;
          bVal = b.last_activity_date;
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredTransactions(filtered);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) return <FaSort className="text-muted" size={12} />;
    return sortDirection === 'asc' ? <FaSortUp className="text-primary" size={12} /> : <FaSortDown className="text-primary" size={12} />;
  };

  const getStageIcon = (stage: string) => {
    switch (stage) {
      case 'listing':
        return <FaHome />;
      case 'under_contract':
        return <FaHandshake />;
      case 'closing':
        return <FaKey />;
      case 'closed':
        return <FaCheck />;
      default:
        return <FaFileContract />;
    }
  };

  const getStageVariant = (stage: string) => {
    switch (stage) {
      case 'listing':
        return 'info';
      case 'under_contract':
        return 'warning';
      case 'closing':
        return 'primary';
      case 'closed':
        return 'success';
      default:
        return 'secondary';
    }
  };

  const getComplianceVariant = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'success';
      case 'warning':
        return 'warning';
      case 'critical':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 1) {
      const minutes = Math.floor(diff / (1000 * 60));
      return `${minutes} minutes ago`;
    } else if (hours < 24) {
      return `${hours} hours ago`;
    } else {
      const days = Math.floor(hours / 24);
      return `${days} days ago`;
    }
  };

  const handleViewTransaction = (transactionId: string) => {
    console.log('Broker Portal - Navigating to transaction details for:', transactionId);
    navigate(`/broker-portal/transactions/${transactionId}`);
  };

  const handleCompleteTransaction = async (transactionId: string, transactionAddress: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to mark this transaction as completed?\n\nProperty: ${transactionAddress}\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) return;

    try {
      console.log('Marking transaction as completed:', transactionId);
      
      // Update transaction stage to completed
      const response = await BrokerApiService.updateTransactionStage(transactionId, 'completed');
      
      if (response.success) {
        // Refresh the transaction list
        await fetchTransactions();
        alert('Transaction marked as completed successfully!');
      } else {
        throw new Error(response.error || 'Failed to update transaction');
      }
    } catch (error: any) {
      console.error('Error completing transaction:', error);
      alert(`Failed to mark transaction as completed: ${error.message || 'Unknown error'}`);
    }
  };


  const uniqueAgents = Array.from(new Set(transactions.map(t => t.agent_id)))
    .map(id => {
      const agent = transactions.find(t => t.agent_id === id);
      return { id, name: agent?.agent_name || '' };
    });

  if (loading) {
    return (
      <Card>
        <Card.Body className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <FaExclamationTriangle className="me-2" />
        {error}
      </Alert>
    );
  }

  return (
    <>
      {/* Summary Stats */}
      {stats && (
        <Row className="mb-4">
          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h3 className="mb-0">{stats.total_active}</h3>
                    <small className="text-muted">Active Transactions</small>
                  </div>
                  <div className="text-primary">
                    <FaFileContract size={30} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h3 className="mb-0">{stats.compliance_alerts}</h3>
                    <small className="text-muted">Compliance Alerts</small>
                  </div>
                  <div className="text-danger">
                    <FaExclamationTriangle size={30} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h3 className="mb-0">{stats.documents_needing_review}</h3>
                    <small className="text-muted">Documents to Review</small>
                  </div>
                  <div className="text-warning">
                    <FaFileAlt size={30} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h3 className="mb-0">{stats.average_compliance_score}%</h3>
                    <small className="text-muted">Avg Compliance</small>
                  </div>
                  <div className="text-success">
                    <FaCheckCircle size={30} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search property, agent, or client..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={2}>
              <Form.Select
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
              >
                <option value="all">All Stages</option>
                <option value="pre_contract">Pre-Contract</option>
                <option value="showing">Showing</option>
                <option value="listing">Listing</option>
                <option value="under_contract">Under Contract</option>
                <option value="closing">Closing</option>
                <option value="closed">Closed</option>
                <option value="completed">Completed</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={complianceFilter}
                onChange={(e) => setComplianceFilter(e.target.value)}
              >
                <option value="all">All Compliance</option>
                <option value="compliant">Compliant</option>
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
              >
                <option value="all">All Agents</option>
                {uniqueAgents.map(agent => (
                  <option key={agent.id} value={agent.id}>{agent.name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2} className="text-end">
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={fetchTransactions}
              >
                <FaSync className="me-1" /> Refresh
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Transactions Table */}
      <Card>
        <div className="table-responsive">
          <Table hover className="mb-0">
            <thead>
              <tr>
                <th>Agent</th>
                <th 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('property_address')}
                >
                  Property {getSortIcon('property_address')}
                </th>
                <th>Client</th>
                <th 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('stage')}
                >
                  Stage {getSortIcon('stage')}
                </th>
                <th 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('compliance_score')}
                >
                  Compliance {getSortIcon('compliance_score')}
                </th>
                <th>Documents</th>
                <th 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('last_activity_date')}
                >
                  Last Activity {getSortIcon('last_activity_date')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-4">
                    <div className="text-muted">
                      <FaFileContract size={48} className="mb-3 opacity-25" />
                      <p>No transactions found matching your filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredTransactions.map(transaction => (
                  <tr key={transaction.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        {transaction.agent_photo ? (
                          <img 
                            src={transaction.agent_photo} 
                            alt={transaction.agent_name}
                            className="rounded-circle me-2 ms-2"
                            style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                          />
                        ) : (
                          <div 
                            className="rounded-circle me-2 ms-2 bg-secondary d-flex align-items-center justify-content-center"
                            style={{ width: '40px', height: '40px', fontSize: '18px', color: 'white' }}
                          >
                            {transaction.agent_name ? transaction.agent_name.charAt(0).toUpperCase() : 'A'}
                          </div>
                        )}
                        <div>
                          <div className="fw-semibold">{transaction.agent_name}</div>
                          <small className="text-muted">{transaction.agent_email}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div className="fw-semibold">{transaction.property_address}</div>
                        <small className="text-muted">
                          <FaMapMarkerAlt className="me-1" />
                          {transaction.property_city}, {transaction.property_state}
                          {transaction.property_price && ` • $${transaction.property_price.toLocaleString()}`}
                        </small>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div>{transaction.client_name}</div>
                        <small className="text-muted">{transaction.client_email}</small>
                      </div>
                    </td>
                    <td>
                      <Badge bg={getStageVariant(transaction.stage)}>
                        {getStageIcon(transaction.stage)} {' '}
                        {transaction.stage.replace('_', ' ').charAt(0).toUpperCase() + transaction.stage.replace('_', ' ').slice(1)}
                      </Badge>
                      <div className="small text-muted mt-1">
                        {transaction.days_in_stage} days
                      </div>
                    </td>
                    <td>
                      <div>
                        <div className="d-flex align-items-center mb-1">
                          <Badge bg={getComplianceVariant(transaction.compliance_status)}>
                            {transaction.compliance_score}%
                          </Badge>
                          {transaction.pending_signatures > 0 && (
                            <Badge bg="warning" className="ms-1">
                              {transaction.pending_signatures} pending
                            </Badge>
                          )}
                        </div>
                        {transaction.missing_documents.length > 0 && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>
                                Missing: {transaction.missing_documents.join(', ')}
                              </Tooltip>
                            }
                          >
                            <small className="text-danger">
                              <FaExclamationTriangle className="me-1" />
                              {transaction.missing_documents.length} missing
                            </small>
                          </OverlayTrigger>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <ProgressBar 
                          now={(transaction.document_count / transaction.total_documents) * 100} 
                          style={{ width: '60px', height: '6px' }}
                          className="me-2"
                        />
                        <small>
                          {transaction.document_count}/{transaction.total_documents}
                        </small>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div className="small">{transaction.last_activity}</div>
                        <small className="text-muted">{formatDate(transaction.last_activity_date)}</small>
                      </div>
                    </td>
                    <td>
                      <ButtonGroup size="sm">
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>View Details</Tooltip>}
                        >
                          <Button 
                            variant="outline-primary"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleViewTransaction(transaction.id);
                            }}
                          >
                            <FaEye />
                          </Button>
                        </OverlayTrigger>
                        {transaction.stage === 'closing' && (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Mark as Completed</Tooltip>}
                          >
                            <Button 
                              variant="outline-success"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleCompleteTransaction(transaction.id, transaction.property_address);
                              }}
                            >
                              <FaCheck />
                            </Button>
                          </OverlayTrigger>
                        )}
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Message Agent</Tooltip>}
                        >
                          <Button 
                            variant="outline-secondary"
                          >
                            <FaEnvelope />
                          </Button>
                        </OverlayTrigger>
                      </ButtonGroup>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* Transaction details now handled by full page routing */}
    </>
  );
};

export default TransactionManagement;